/**
 * HistoricalTabMockup.js - COMPLETE Implementation
 *
 * COMPLETE mockup for Historical tab with ALL content from live dashboard
 * plus the 3 audit enhancements applied.
 *
 * ENHANCEMENTS FROM AUDIT:
 * 1. Summary KPI cards at top before charts
 * 2. Tabbed sub-navigation for FUNDINGS/OUTREACH/SUBMISSIONS/PACK SOURCE/UNDERWRITING
 * 3. Leaderboards moved higher with sticky position
 *
 * LIVE DASHBOARD CONTENT INCLUDED:
 * - FUNDINGS: Revenue Trends (1 chart)
 * - OUTREACH: Call Volume, Activity Points, Apps-In (3 charts)
 * - SUBMISSIONS: Avg per Funded, Avg per Underwriting, Success Rate, 4 comparison bars (7 items)
 * - PACK SOURCE: Month selector, 4 pie charts, stacked bar (6 items)
 * - UNDERWRITING: 5 trend charts
 * - TEAM OVERVIEW: Radar + Bubble charts
 * - DECLINED/LOST: Analysis cards + table
 * - LEADERBOARDS: 3 ranking cards
 *
 * Created: December 4, 2025
 * Last Updated: December 4, 2025 - COMPLETE REBUILD
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, ZAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList
} from 'recharts';

// ============================================================================
// SAMPLE DATA FOR ALL CHARTS
// ============================================================================

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const REPS = ['Patrick McTiernan', 'Jorge Verdesoto', 'Marcin Maleski', 'Matt Vecchione', 'Damian Pomykala'];
const REP_COLORS = {
  'Patrick McTiernan': '#3b82f6',
  'Jorge Verdesoto': '#10b981',
  'Marcin Maleski': '#8b5cf6',
  'Matt Vecchione': '#f59e0b',
  'Damian Pomykala': '#ef4444',
  'Team Average': '#000000'
};

// Revenue Trends Data (FUNDINGS)
const REVENUE_TRENDS_DATA = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': 45000 + Math.random() * 30000,
  'Jorge Verdesoto': 38000 + Math.random() * 25000,
  'Marcin Maleski': 52000 + Math.random() * 35000,
  'Matt Vecchione': 41000 + Math.random() * 28000,
  'Damian Pomykala': 35000 + Math.random() * 22000,
  'Team Average': 42000 + Math.random() * 15000
}));

// Call Volume Trends Data (OUTREACH)
const CALL_VOLUME_DATA = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': 180 + Math.floor(Math.random() * 80),
  'Jorge Verdesoto': 150 + Math.floor(Math.random() * 60),
  'Marcin Maleski': 200 + Math.floor(Math.random() * 100),
  'Matt Vecchione': 170 + Math.floor(Math.random() * 70),
  'Damian Pomykala': 140 + Math.floor(Math.random() * 50),
  'Team Average': 168 + Math.floor(Math.random() * 40)
}));

// Activity Points Trends Data (OUTREACH)
const ACTIVITY_POINTS_DATA = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': 320 + Math.floor(Math.random() * 150),
  'Jorge Verdesoto': 280 + Math.floor(Math.random() * 120),
  'Marcin Maleski': 380 + Math.floor(Math.random() * 180),
  'Matt Vecchione': 300 + Math.floor(Math.random() * 130),
  'Damian Pomykala': 250 + Math.floor(Math.random() * 100),
  'Team Average': 306 + Math.floor(Math.random() * 80)
}));

// Apps-In Trends Data (OUTREACH)
const APPS_IN_DATA = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': 12 + Math.floor(Math.random() * 8),
  'Jorge Verdesoto': 10 + Math.floor(Math.random() * 6),
  'Marcin Maleski': 15 + Math.floor(Math.random() * 10),
  'Matt Vecchione': 11 + Math.floor(Math.random() * 7),
  'Damian Pomykala': 8 + Math.floor(Math.random() * 5),
  'Team Average': 11.2 + Math.random() * 4
}));

// Avg Submissions per Funded Deal (SUBMISSIONS)
const AVG_SUBS_PER_FUNDED_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 2.5 + Math.random() * 1.5,
  'Jorge Verdesoto': 3.0 + Math.random() * 1.8,
  'Marcin Maleski': 2.2 + Math.random() * 1.2,
  'Matt Vecchione': 2.8 + Math.random() * 1.6,
  'Damian Pomykala': 3.2 + Math.random() * 2.0,
  'Team Average': 2.74 + Math.random() * 0.8
}));

// Avg Submissions per Underwriting (SUBMISSIONS)
const AVG_SUBS_PER_UW_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 2.8 + Math.random() * 1.5,
  'Jorge Verdesoto': 3.2 + Math.random() * 1.8,
  'Marcin Maleski': 2.5 + Math.random() * 1.2,
  'Matt Vecchione': 3.0 + Math.random() * 1.6,
  'Damian Pomykala': 3.5 + Math.random() * 2.0,
  'Team Average': 3.0 + Math.random() * 0.8
}));

// Submission Success Rate (SUBMISSIONS)
const SUBMISSION_SUCCESS_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 35 + Math.random() * 20,
  'Jorge Verdesoto': 30 + Math.random() * 18,
  'Marcin Maleski': 40 + Math.random() * 22,
  'Matt Vecchione': 32 + Math.random() * 19,
  'Damian Pomykala': 28 + Math.random() * 16,
  'Team Average': 33 + Math.random() * 10
}));

// Rep Submission Comparison (SUBMISSIONS)
const REP_SUBMISSION_COMPARISON = [
  { name: 'Patrick', avgPerFunded: 2.8, declineRate: 22, avgPerDeal: 3.2, openSubmissions: 5 },
  { name: 'Jorge', avgPerFunded: 3.1, declineRate: 28, avgPerDeal: 3.5, openSubmissions: 7 },
  { name: 'Marcin', avgPerFunded: 2.4, declineRate: 18, avgPerDeal: 2.8, openSubmissions: 3 },
  { name: 'Matt', avgPerFunded: 2.9, declineRate: 25, avgPerDeal: 3.3, openSubmissions: 6 },
  { name: 'Damian', avgPerFunded: 3.4, declineRate: 32, avgPerDeal: 3.8, openSubmissions: 8 }
];

// Pack Source Breakdown (PACK SOURCE)
const PACK_SOURCE_DATA = {
  appsIn: [
    { name: 'Inbound', value: 45 },
    { name: 'Outbound', value: 30 },
    { name: 'Referral', value: 15 },
    { name: 'Missing', value: 10 }
  ],
  approved: [
    { name: 'Inbound', value: 38 },
    { name: 'Outbound', value: 25 },
    { name: 'Referral', value: 18 },
    { name: 'Missing', value: 8 }
  ],
  funded: [
    { name: 'Inbound', value: 32 },
    { name: 'Outbound', value: 22 },
    { name: 'Referral', value: 20 },
    { name: 'Missing', value: 6 }
  ],
  declined: [
    { name: 'Inbound', value: 15 },
    { name: 'Outbound', value: 12 },
    { name: 'Referral', value: 5 },
    { name: 'Missing', value: 3 }
  ]
};
const PACK_SOURCE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#9ca3af'];

// Pack Source Trends (PACK SOURCE)
const PACK_SOURCE_TRENDS = SHORT_MONTHS.map((month) => ({
  month,
  Inbound: 35 + Math.random() * 20,
  Outbound: 25 + Math.random() * 15,
  Referral: 15 + Math.random() * 10,
  Missing: 5 + Math.random() * 8
}));

// Apps -> Approved Trends (UNDERWRITING)
const APPROVED_TRENDS_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 10 + Math.floor(Math.random() * 6),
  'Jorge Verdesoto': 8 + Math.floor(Math.random() * 5),
  'Marcin Maleski': 12 + Math.floor(Math.random() * 7),
  'Matt Vecchione': 9 + Math.floor(Math.random() * 5),
  'Damian Pomykala': 7 + Math.floor(Math.random() * 4),
  'Team Average': 9.2 + Math.random() * 3
}));

// Approval Rate % Trends (UNDERWRITING)
const APPROVAL_RATE_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 70 + Math.random() * 15,
  'Jorge Verdesoto': 65 + Math.random() * 18,
  'Marcin Maleski': 75 + Math.random() * 12,
  'Matt Vecchione': 68 + Math.random() * 16,
  'Damian Pomykala': 62 + Math.random() * 20,
  'Team Average': 68 + Math.random() * 8
}));

// Apps -> Funded Rate (UNDERWRITING)
const APPS_TO_FUNDED_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 45 + Math.random() * 20,
  'Jorge Verdesoto': 40 + Math.random() * 18,
  'Marcin Maleski': 50 + Math.random() * 22,
  'Matt Vecchione': 42 + Math.random() * 19,
  'Damian Pomykala': 38 + Math.random() * 16,
  'Team Average': 43 + Math.random() * 10
}));

// Funded Count Trends (UNDERWRITING)
const FUNDED_COUNT_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 5 + Math.floor(Math.random() * 4),
  'Jorge Verdesoto': 4 + Math.floor(Math.random() * 3),
  'Marcin Maleski': 6 + Math.floor(Math.random() * 5),
  'Matt Vecchione': 5 + Math.floor(Math.random() * 3),
  'Damian Pomykala': 3 + Math.floor(Math.random() * 3),
  'Team Average': 4.6 + Math.random() * 2
}));

// Avg Deal Size Trends (UNDERWRITING)
const AVG_DEAL_SIZE_DATA = SHORT_MONTHS.map((month) => ({
  month,
  'Patrick McTiernan': 8500 + Math.random() * 4000,
  'Jorge Verdesoto': 7500 + Math.random() * 3500,
  'Marcin Maleski': 9500 + Math.random() * 5000,
  'Matt Vecchione': 8000 + Math.random() * 3800,
  'Damian Pomykala': 7000 + Math.random() * 3200,
  'Team Average': 8100 + Math.random() * 2000
}));

// Radar Chart Data (TEAM OVERVIEW)
const RADAR_DATA = [
  { metric: 'Call Volume', 'Patrick McTiernan': 75, 'Jorge Verdesoto': 65, 'Marcin Maleski': 85, 'Matt Vecchione': 70, 'Damian Pomykala': 60, 'Team Average': 71 },
  { metric: 'Calls->Apps %', 'Patrick McTiernan': 68, 'Jorge Verdesoto': 72, 'Marcin Maleski': 78, 'Matt Vecchione': 65, 'Damian Pomykala': 58, 'Team Average': 68.2 },
  { metric: 'Apps->Approved %', 'Patrick McTiernan': 82, 'Jorge Verdesoto': 75, 'Marcin Maleski': 88, 'Matt Vecchione': 78, 'Damian Pomykala': 70, 'Team Average': 78.6 },
  { metric: 'Approved->Funded %', 'Patrick McTiernan': 70, 'Jorge Verdesoto': 68, 'Marcin Maleski': 75, 'Matt Vecchione': 72, 'Damian Pomykala': 65, 'Team Average': 70 },
  { metric: 'Avg Deal Size', 'Patrick McTiernan': 72, 'Jorge Verdesoto': 65, 'Marcin Maleski': 80, 'Matt Vecchione': 68, 'Damian Pomykala': 60, 'Team Average': 69 },
  { metric: 'Revenue', 'Patrick McTiernan': 78, 'Jorge Verdesoto': 70, 'Marcin Maleski': 90, 'Matt Vecchione': 75, 'Damian Pomykala': 62, 'Team Average': 75 }
];

// Bubble Chart Data (TEAM OVERVIEW)
const BUBBLE_DATA = REPS.flatMap(rep =>
  SHORT_MONTHS.slice(-6).map(month => ({
    rep,
    month,
    x: 200 + Math.random() * 300, // Activity Volume
    y: 2 + Math.random() * 5, // Conversion %
    z: 20000 + Math.random() * 60000, // Revenue (bubble size)
    revenue: 20000 + Math.random() * 60000
  }))
);

// Declined/Lost Data
const DECLINED_LOST_DATA = {
  totalCount: 23,
  totalValue: 287500,
  topReasons: [
    { reason: 'Credit Score Too Low', count: 8 },
    { reason: 'Time in Business', count: 6 },
    { reason: 'Insufficient Revenue', count: 5 },
    { reason: 'Industry Risk', count: 4 }
  ],
  recentDeclines: [
    { Account: 'ABC Corp', Opportunity: 'Working Capital', Amount: 45000, Stage_Detail: 'Credit Score', Date: '2025-12-02' },
    { Account: 'XYZ LLC', Opportunity: 'Equipment Finance', Amount: 32000, Stage_Detail: 'Time in Business', Date: '2025-12-01' },
    { Account: 'Tech Solutions', Opportunity: 'Line of Credit', Amount: 28000, Stage_Detail: 'Insufficient Revenue', Date: '2025-11-28' }
  ]
};

// Leaderboard Data
const LEADERBOARD_DATA = {
  byRevenue: [
    { rep: 'Marcin Maleski', totalRevenue: 625000 },
    { rep: 'Patrick McTiernan', totalRevenue: 542000 },
    { rep: 'Matt Vecchione', totalRevenue: 498000 },
    { rep: 'Jorge Verdesoto', totalRevenue: 456000 },
    { rep: 'Damian Pomykala', totalRevenue: 385000 }
  ],
  byFundedCount: [
    { rep: 'Marcin Maleski', fundedCount: 72 },
    { rep: 'Patrick McTiernan', fundedCount: 64 },
    { rep: 'Matt Vecchione', fundedCount: 58 },
    { rep: 'Jorge Verdesoto', fundedCount: 52 },
    { rep: 'Damian Pomykala', fundedCount: 41 }
  ],
  byConversionRate: [
    { rep: 'Marcin Maleski', conversionRate: 52.3 },
    { rep: 'Patrick McTiernan', conversionRate: 48.7 },
    { rep: 'Matt Vecchione', conversionRate: 45.2 },
    { rep: 'Jorge Verdesoto', conversionRate: 43.8 },
    { rep: 'Damian Pomykala', conversionRate: 38.5 }
  ]
};

// Summary KPIs
const SUMMARY_KPIS = {
  totalRevenue: 2506000,
  totalFunded: 287,
  avgDealSize: 8732,
  appsToFundedRate: 44.2,
  activityVolume: 18420
};

// ============================================================================
// SINGLE REP DEEP-DIVE SAMPLE DATA
// ============================================================================

// Goal Performance Data (per rep, per month)
const SINGLE_REP_GOALS = {
  'Patrick McTiernan': {
    'October': { goal: 85000, actual: 78500, calls: 245, sms: 180, apps: 14 },
    'November': { goal: 90000, actual: 92300, calls: 268, sms: 195, apps: 16 },
    'December': { goal: 95000, actual: 67800, calls: 198, sms: 145, apps: 11 }
  },
  'Jorge Verdesoto': {
    'October': { goal: 75000, actual: 71200, calls: 215, sms: 165, apps: 12 },
    'November': { goal: 80000, actual: 76800, calls: 228, sms: 172, apps: 13 },
    'December': { goal: 85000, actual: 58400, calls: 175, sms: 128, apps: 9 }
  },
  'Marcin Maleski': {
    'October': { goal: 100000, actual: 108500, calls: 285, sms: 210, apps: 18 },
    'November': { goal: 105000, actual: 112000, calls: 298, sms: 225, apps: 20 },
    'December': { goal: 110000, actual: 82500, calls: 218, sms: 168, apps: 14 }
  },
  'Matt Vecchione': {
    'October': { goal: 80000, actual: 74600, calls: 232, sms: 175, apps: 13 },
    'November': { goal: 85000, actual: 88200, calls: 255, sms: 188, apps: 15 },
    'December': { goal: 90000, actual: 62100, calls: 185, sms: 138, apps: 10 }
  },
  'Damian Pomykala': {
    'October': { goal: 70000, actual: 62800, calls: 195, sms: 148, apps: 10 },
    'November': { goal: 75000, actual: 71500, calls: 208, sms: 158, apps: 12 },
    'December': { goal: 80000, actual: 48200, calls: 155, sms: 115, apps: 8 }
  }
};

// Conversion Rate Trends (per rep, by month) - for Single Rep charts
const SINGLE_REP_CONVERSION_TRENDS = SHORT_MONTHS.map((month, i) => ({
  month,
  // Per-rep conversion rates
  'Patrick McTiernan': { callsToApps: 5.2 + Math.random() * 2, smsToApps: 3.8 + Math.random() * 1.5, appsToApproved: 72 + Math.random() * 10, approvedToFunded: 65 + Math.random() * 12 },
  'Jorge Verdesoto': { callsToApps: 4.8 + Math.random() * 1.8, smsToApps: 3.5 + Math.random() * 1.2, appsToApproved: 68 + Math.random() * 12, approvedToFunded: 62 + Math.random() * 10 },
  'Marcin Maleski': { callsToApps: 5.8 + Math.random() * 2.2, smsToApps: 4.2 + Math.random() * 1.8, appsToApproved: 78 + Math.random() * 8, approvedToFunded: 70 + Math.random() * 10 },
  'Matt Vecchione': { callsToApps: 5.0 + Math.random() * 1.8, smsToApps: 3.6 + Math.random() * 1.4, appsToApproved: 70 + Math.random() * 10, approvedToFunded: 64 + Math.random() * 11 },
  'Damian Pomykala': { callsToApps: 4.5 + Math.random() * 1.5, smsToApps: 3.2 + Math.random() * 1.0, appsToApproved: 65 + Math.random() * 14, approvedToFunded: 58 + Math.random() * 12 },
  'Team Average': { callsToApps: 5.06 + Math.random() * 0.8, smsToApps: 3.66 + Math.random() * 0.6, appsToApproved: 70.6 + Math.random() * 5, approvedToFunded: 63.8 + Math.random() * 6 }
}));

// Activity Volume Trends (per rep, by month) - for Single Rep charts
// ENHANCED: Added talkTime (minutes) per 12-4-25 scope doc
const SINGLE_REP_ACTIVITY_TRENDS = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': { calls: 240 + Math.floor(Math.random() * 60), sms: 175 + Math.floor(Math.random() * 45), talkTime: 180 + Math.floor(Math.random() * 90), total: 415 + Math.floor(Math.random() * 100) },
  'Jorge Verdesoto': { calls: 210 + Math.floor(Math.random() * 50), sms: 160 + Math.floor(Math.random() * 40), talkTime: 155 + Math.floor(Math.random() * 75), total: 370 + Math.floor(Math.random() * 85) },
  'Marcin Maleski': { calls: 280 + Math.floor(Math.random() * 70), sms: 205 + Math.floor(Math.random() * 55), talkTime: 220 + Math.floor(Math.random() * 110), total: 485 + Math.floor(Math.random() * 120) },
  'Matt Vecchione': { calls: 225 + Math.floor(Math.random() * 55), sms: 170 + Math.floor(Math.random() * 42), talkTime: 170 + Math.floor(Math.random() * 85), total: 395 + Math.floor(Math.random() * 90) },
  'Damian Pomykala': { calls: 190 + Math.floor(Math.random() * 45), sms: 145 + Math.floor(Math.random() * 35), talkTime: 140 + Math.floor(Math.random() * 70), total: 335 + Math.floor(Math.random() * 75) },
  'Team Average': { calls: 229 + Math.floor(Math.random() * 25), sms: 171 + Math.floor(Math.random() * 20), talkTime: 173 + Math.floor(Math.random() * 40), total: 400 + Math.floor(Math.random() * 40) }
}));

// Pack Source Conversion Trends (Apps → Funded % by source) - Enhancement #1
const PACK_SOURCE_CONVERSION_TRENDS = SHORT_MONTHS.map((month, i) => ({
  month,
  Inbound: 42 + Math.random() * 18,
  Outbound: 35 + Math.random() * 15,
  Referral: 55 + Math.random() * 20
}));

// Deal Performance Trends (for new 💲 Deal Performance section) - Enhancement #4
const SINGLE_REP_DEAL_PERFORMANCE = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': { fundedAmount: 75000 + Math.random() * 35000, revenue: 11000 + Math.random() * 6000, dealsFunded: 6 + Math.floor(Math.random() * 4), avgDealSize: 8200 + Math.random() * 2500 },
  'Jorge Verdesoto': { fundedAmount: 65000 + Math.random() * 30000, revenue: 9500 + Math.random() * 5000, dealsFunded: 5 + Math.floor(Math.random() * 3), avgDealSize: 7500 + Math.random() * 2200 },
  'Marcin Maleski': { fundedAmount: 90000 + Math.random() * 40000, revenue: 13500 + Math.random() * 7000, dealsFunded: 7 + Math.floor(Math.random() * 5), avgDealSize: 9500 + Math.random() * 3000 },
  'Matt Vecchione': { fundedAmount: 70000 + Math.random() * 32000, revenue: 10500 + Math.random() * 5500, dealsFunded: 5 + Math.floor(Math.random() * 4), avgDealSize: 7800 + Math.random() * 2400 },
  'Damian Pomykala': { fundedAmount: 55000 + Math.random() * 25000, revenue: 8000 + Math.random() * 4000, dealsFunded: 4 + Math.floor(Math.random() * 3), avgDealSize: 6800 + Math.random() * 2000 },
  'Team Average': { fundedAmount: 71000 + Math.random() * 15000, revenue: 10500 + Math.random() * 3000, dealsFunded: 5.4 + Math.random() * 1.5, avgDealSize: 7960 + Math.random() * 1200 }
}));

// Deal Size Trends (per rep, by month) - for Single Rep charts
const SINGLE_REP_DEAL_SIZE_TRENDS = SHORT_MONTHS.map((month, i) => ({
  month,
  'Patrick McTiernan': { avgDealSize: 8200 + Math.random() * 2500, revenue: 78000 + Math.random() * 25000 },
  'Jorge Verdesoto': { avgDealSize: 7500 + Math.random() * 2200, revenue: 68000 + Math.random() * 22000 },
  'Marcin Maleski': { avgDealSize: 9500 + Math.random() * 3000, revenue: 95000 + Math.random() * 30000 },
  'Matt Vecchione': { avgDealSize: 7800 + Math.random() * 2400, revenue: 72000 + Math.random() * 24000 },
  'Damian Pomykala': { avgDealSize: 6800 + Math.random() * 2000, revenue: 58000 + Math.random() * 18000 },
  'Team Average': { avgDealSize: 7960 + Math.random() * 1200, revenue: 74200 + Math.random() * 12000 }
}));

// Rep Insights (calculated summaries per rep)
const SINGLE_REP_INSIGHTS = {
  'Patrick McTiernan': {
    trendDirection: 'improving',
    trendPercent: 18,
    bestMonth: 'November',
    bestRevenue: 92300,
    biggestGap: 'Calls → Apps',
    biggestGapValue: 5.4
  },
  'Jorge Verdesoto': {
    trendDirection: 'declining',
    trendPercent: -6,
    bestMonth: 'November',
    bestRevenue: 76800,
    biggestGap: 'Approved → Funded',
    biggestGapValue: 62
  },
  'Marcin Maleski': {
    trendDirection: 'improving',
    trendPercent: 24,
    bestMonth: 'November',
    bestRevenue: 112000,
    biggestGap: 'SMS → Apps',
    biggestGapValue: 4.5
  },
  'Matt Vecchione': {
    trendDirection: 'improving',
    trendPercent: 12,
    bestMonth: 'November',
    bestRevenue: 88200,
    biggestGap: 'Apps → Approved',
    biggestGapValue: 70
  },
  'Damian Pomykala': {
    trendDirection: 'declining',
    trendPercent: -8,
    bestMonth: 'November',
    bestRevenue: 71500,
    biggestGap: 'Calls → Apps',
    biggestGapValue: 4.8
  }
};

// Pack Source Conversion by Rep (for Single Rep)
const SINGLE_REP_PACK_SOURCE = {
  'Patrick McTiernan': [
    { source: 'Inbound', appsIn: 8, approved: 6, conversionRate: 75 },
    { source: 'Outbound', appsIn: 5, approved: 3, conversionRate: 60 },
    { source: 'Referral', appsIn: 3, approved: 3, conversionRate: 100 },
    { source: 'Missing', appsIn: 2, approved: 1, conversionRate: 50 }
  ],
  'Jorge Verdesoto': [
    { source: 'Inbound', appsIn: 7, approved: 5, conversionRate: 71 },
    { source: 'Outbound', appsIn: 4, approved: 2, conversionRate: 50 },
    { source: 'Referral', appsIn: 2, approved: 2, conversionRate: 100 },
    { source: 'Missing', appsIn: 1, approved: 0, conversionRate: 0 }
  ],
  'Marcin Maleski': [
    { source: 'Inbound', appsIn: 10, approved: 8, conversionRate: 80 },
    { source: 'Outbound', appsIn: 6, approved: 5, conversionRate: 83 },
    { source: 'Referral', appsIn: 4, approved: 4, conversionRate: 100 },
    { source: 'Missing', appsIn: 2, approved: 1, conversionRate: 50 }
  ],
  'Matt Vecchione': [
    { source: 'Inbound', appsIn: 8, approved: 6, conversionRate: 75 },
    { source: 'Outbound', appsIn: 4, approved: 3, conversionRate: 75 },
    { source: 'Referral', appsIn: 3, approved: 2, conversionRate: 67 },
    { source: 'Missing', appsIn: 1, approved: 0, conversionRate: 0 }
  ],
  'Damian Pomykala': [
    { source: 'Inbound', appsIn: 6, approved: 4, conversionRate: 67 },
    { source: 'Outbound', appsIn: 3, approved: 1, conversionRate: 33 },
    { source: 'Referral', appsIn: 2, approved: 2, conversionRate: 100 },
    { source: 'Missing', appsIn: 1, approved: 0, conversionRate: 0 }
  ]
};

// Single Rep Submission Metrics (per rep)
const SINGLE_REP_SUBMISSIONS = {
  'Patrick McTiernan': { avgPerFunded: 2.8, avgPerUW: 3.2, avgPerDeclined: 4.1, successRate: 38, openSubmissions: 5, totalSubmissions: 42, wonSubmissions: 16, lostSubmissions: 21 },
  'Jorge Verdesoto': { avgPerFunded: 3.1, avgPerUW: 3.5, avgPerDeclined: 4.5, successRate: 32, openSubmissions: 7, totalSubmissions: 38, wonSubmissions: 12, lostSubmissions: 19 },
  'Marcin Maleski': { avgPerFunded: 2.4, avgPerUW: 2.8, avgPerDeclined: 3.6, successRate: 45, openSubmissions: 3, totalSubmissions: 55, wonSubmissions: 25, lostSubmissions: 27 },
  'Matt Vecchione': { avgPerFunded: 2.9, avgPerUW: 3.3, avgPerDeclined: 4.2, successRate: 35, openSubmissions: 6, totalSubmissions: 45, wonSubmissions: 16, lostSubmissions: 23 },
  'Damian Pomykala': { avgPerFunded: 3.4, avgPerUW: 3.8, avgPerDeclined: 4.8, successRate: 28, openSubmissions: 8, totalSubmissions: 32, wonSubmissions: 9, lostSubmissions: 15 }
};

// ============================================================================
// TEAM SNAPSHOT SAMPLE DATA (12-4-25)
// ============================================================================

// Team Submission Trends (aggregate across all reps per month)
const TEAM_SUBMISSION_TRENDS = SHORT_MONTHS.map((month, i) => ({
  month,
  submissionsWon: 12 + Math.floor(Math.random() * 8),
  submissionsLost: 8 + Math.floor(Math.random() * 6),
  totalDeclines: 15 + Math.floor(Math.random() * 10),
  avgSubmissionsPerDeal: 2.5 + Math.random() * 1.5,
  successRate: 35 + Math.random() * 20
}));

// Team Aggregates Data (all-time totals)
const TEAM_AGGREGATES = {
  totalRevenue: 2506000,
  totalFundedAmount: 8250000,
  totalFundedUnits: 287,
  avgDealSize: 28745,
  quotaAttainment: 87.5,  // Current month
  activityAttainment: 92.3  // Current month
};

// Team Revenue vs Goal (monthly)
const TEAM_REVENUE_VS_GOAL = SHORT_MONTHS.map((month, i) => ({
  month,
  revenue: 180000 + Math.random() * 80000,
  goal: 200000 + Math.random() * 50000
}));

// Team Funded Deals (monthly counts)
const TEAM_FUNDED_DEALS = SHORT_MONTHS.map((month, i) => ({
  month,
  funded: 18 + Math.floor(Math.random() * 12)
}));

// Team Apps In (monthly counts)
const TEAM_APPS_IN = SHORT_MONTHS.map((month, i) => ({
  month,
  appsIn: 45 + Math.floor(Math.random() * 25)
}));

// Team Activity Volume (calls + SMS per month)
const TEAM_ACTIVITY_VOLUME = SHORT_MONTHS.map((month, i) => {
  const calls = 850 + Math.floor(Math.random() * 250);
  const sms = 650 + Math.floor(Math.random() * 200);
  const total = calls + sms;
  return {
    month,
    calls,
    sms,
    total,
    callsPct: Math.round((calls / total) * 100),
    smsPct: Math.round((sms / total) * 100)
  };
});

// Pack Source Volume (team-wide)
const TEAM_PACK_SOURCE_VOLUME = [
  { name: 'Inbound', appsIn: 145, approved: 118, funded: 92 },
  { name: 'Outbound', appsIn: 98, approved: 72, funded: 58 },
  { name: 'Referral', appsIn: 62, approved: 55, funded: 48 },
  { name: 'Missing', appsIn: 35, approved: 22, funded: 15 }
];

// Pack Source Revenue (team-wide)
const TEAM_PACK_SOURCE_REVENUE = [
  { name: 'Inbound', revenue: 1250000 },
  { name: 'Outbound', revenue: 820000 },
  { name: 'Referral', revenue: 680000 },
  { name: 'Missing', revenue: 185000 }
];

// Pack Source Trend (already exists as PACK_SOURCE_TRENDS - reuse)

// Monthly Performance by Rep (summary data)
const TEAM_MONTHLY_PERFORMANCE = REPS.map(rep => {
  const months = ['October', 'November', 'December'];
  return {
    rep,
    totalRevenue: 180000 + Math.random() * 120000,
    totalFunded: 15 + Math.floor(Math.random() * 12),
    totalApps: 35 + Math.floor(Math.random() * 20),
    totalCalls: 600 + Math.floor(Math.random() * 300),
    avgConversion: 38 + Math.random() * 18,
    avgGoalAttainment: 75 + Math.random() * 30,
    monthlyData: months.map(month => ({
      month,
      revenue: 55000 + Math.random() * 40000,
      funded: 4 + Math.floor(Math.random() * 5),
      appsIn: 10 + Math.floor(Math.random() * 8),
      calls: 180 + Math.floor(Math.random() * 100),
      conversion: 35 + Math.random() * 20,
      goalAttainment: 70 + Math.random() * 35
    }))
  };
});

// Rankings Data (for leaderboards)
const TEAM_RANKINGS = {
  byRevenue: REPS.map(rep => ({
    rep,
    value: 85000 + Math.random() * 50000
  })).sort((a, b) => b.value - a.value),

  byFunded: REPS.map(rep => ({
    rep,
    value: 5 + Math.floor(Math.random() * 8)
  })).sort((a, b) => b.value - a.value),

  byQuota: REPS.map(rep => ({
    rep,
    value: 70 + Math.random() * 40
  })).sort((a, b) => b.value - a.value),

  byActivity: REPS.map(rep => ({
    rep,
    value: 350 + Math.floor(Math.random() * 200)
  })).sort((a, b) => b.value - a.value),

  byConversion: REPS.map(rep => ({
    rep,
    value: 38 + Math.random() * 20
  })).sort((a, b) => b.value - a.value),

  byDealSize: REPS.map(rep => ({
    rep,
    value: 7500 + Math.random() * 4000
  })).sort((a, b) => b.value - a.value),

  byConsistency: REPS.map(rep => ({
    rep,
    value: 72000 + Math.random() * 30000  // Avg monthly revenue (higher = more consistent)
  })).sort((a, b) => b.value - a.value)
};

// Summary KPIs for Team Snapshot (Enhancement #6)
const TEAM_SNAPSHOT_KPIS = {
  totalRevenue: 2506000,
  totalFundedDeals: 287,
  teamAvgConversion: 44.2,
  currentMonthAttainment: 87.5,
  topPerformer: 'Marcin Maleski'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatCurrency = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '$0';
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function HistoricalTabMockup({ initialSubtab = 'rep-vs-rep' }) {
  const [activeSubtab, setActiveSubtab] = useState(initialSubtab);

  // Sync activeSubtab when initialSubtab prop changes (fixes MockupHub sidebar navigation)
  useEffect(() => {
    setActiveSubtab(initialSubtab);
  }, [initialSubtab]);

  const [activeRepTab, setActiveRepTab] = useState(REPS[0]);
  const [activeSectionTab, setActiveSectionTab] = useState('fundings');
  const [packSourceMonth, setPackSourceMonth] = useState('December');
  const [hiddenReps, setHiddenReps] = useState([]);
  // Single Rep Deep-Dive states (V3 redesign - scrollable sections, not tabs)
  const [selectedSingleRep, setSelectedSingleRep] = useState(REPS[0]);
  const [activeSingleRepSection, setActiveSingleRepSection] = useState('snapshot');
  const [collapsedSingleRepSections, setCollapsedSingleRepSections] = useState({});

  // Team Snapshot states (12-4-25 - Full implementation with 6 enhancements)
  const [activeTeamSection, setActiveTeamSection] = useState('aggregates');
  const [collapsedTeamSections, setCollapsedTeamSections] = useState({});
  const [packSourceViewTab, setPackSourceViewTab] = useState('volume');  // volume | revenue | trend
  const [expandedRepRows, setExpandedRepRows] = useState({});  // For monthly performance table

  // DEPRECATED: Old tab state (kept for backwards compatibility with disabled code)
  // eslint-disable-next-line no-unused-vars
  const [singleRepSectionTab, setSingleRepSectionTab] = useState('snapshot');

  // Section refs for scroll-to functionality
  const singleRepSectionRefs = {
    snapshot: useRef(null),
    dealPerformance: useRef(null),  // NEW: Enhancement #4
    conversions: useRef(null),
    activity: useRef(null),
    submissions: useRef(null)
  };

  // Team Snapshot section refs (Enhancement #3 - Sticky section tabs)
  const teamSectionRefs = {
    summary: useRef(null),
    aggregates: useRef(null),
    submissions: useRef(null),
    rankings: useRef(null),
    funding: useRef(null),
    activity: useRef(null),
    packSource: useRef(null),
    table: useRef(null)
  };

  // Rep v Rep section refs (12-5-25 - Converting to scroll-to pattern)
  const repVsRepSectionRefs = {
    fundings: useRef(null),
    outreach: useRef(null),
    submissions: useRef(null),
    packsource: useRef(null),
    underwriting: useRef(null)
  };

  // Scroll to section function
  const scrollToSingleRepSection = (sectionId) => {
    setActiveSingleRepSection(sectionId);
    singleRepSectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Toggle section collapse
  const toggleSingleRepSection = (sectionId) => {
    setCollapsedSingleRepSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Team Snapshot helper functions (12-4-25)
  const scrollToTeamSection = (sectionId) => {
    setActiveTeamSection(sectionId);
    teamSectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleTeamSection = (sectionId) => {
    setCollapsedTeamSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleRepRow = (rep) => {
    setExpandedRepRows(prev => ({
      ...prev,
      [rep]: !prev[rep]
    }));
  };

  // Rep v Rep scroll function (12-5-25 - Converting to scroll-to pattern)
  const scrollToRepVsRepSection = (sectionId) => {
    setActiveSectionTab(sectionId);
    repVsRepSectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // IntersectionObserver to track visible sections and update sticky nav (12-5-25)
  useEffect(() => {
    if (activeSubtab !== 'rep-vs-rep') return;

    const sectionIds = ['fundings', 'outreach', 'submissions', 'packsource', 'underwriting'];
    const observers = [];

    sectionIds.forEach(id => {
      const ref = repVsRepSectionRefs[id];
      if (ref?.current) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                setActiveSectionTab(id);
              }
            });
          },
          { threshold: 0.3, rootMargin: '-100px 0px -50% 0px' }
        );
        observer.observe(ref.current);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [activeSubtab]);

  useEffect(() => {
    document.title = 'Historical Tab Mockup - UX/UI Audit';
  }, []);

  // Filter visible reps
  const displayReps = [...REPS, 'Team Average'].filter(rep => !hiddenReps.includes(rep));

  // Custom Legend with click to hide
  const renderCustomLegend = (props) => {
    const { payload } = props;
    if (!payload) return null;

    return (
      <ul style={{ listStyle: 'none', display: 'flex', gap: '15px', justifyContent: 'center', padding: '10px 0', margin: 0, flexWrap: 'wrap' }}>
        {payload.map((entry, index) => {
          const isHidden = hiddenReps.includes(entry.dataKey);
          const isTeamAverage = entry.dataKey === 'Team Average';

          return (
            <li
              key={`item-${index}`}
              onClick={() => {
                if (isTeamAverage) return;
                setHiddenReps(prev => prev.includes(entry.dataKey)
                  ? prev.filter(r => r !== entry.dataKey)
                  : [...prev, entry.dataKey]
                );
              }}
              title={isTeamAverage ? 'Team Average (always visible)' : isHidden ? 'Click to show' : 'Click to hide'}
              style={{
                cursor: isTeamAverage ? 'default' : 'pointer',
                opacity: isHidden ? 0.4 : 1,
                textDecoration: isHidden ? 'line-through' : 'none',
                color: isHidden ? '#9ca3af' : entry.color,
                fontWeight: isHidden ? 'normal' : 'bold',
                fontSize: '14px',
                fontStyle: isTeamAverage ? 'italic' : 'normal',
                display: 'flex',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: entry.color,
                marginRight: '6px',
                borderRadius: '2px',
                opacity: isHidden ? 0.3 : 1
              }}></span>
              {isTeamAverage ? 'Team Avg' : entry.value}
            </li>
          );
        })}
      </ul>
    );
  };

  // ============================================================================
  // REP V REP SUBTAB - COMPLETE IMPLEMENTATION
  // ============================================================================

  const renderRepVsRepSubtab = () => {
    return (
      <div>
        {/* ========== ENHANCEMENT #2: Tabbed Section Navigation (STICKY - AT TOP) ========== */}
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 10,
          backgroundColor: '#f1f5f9',
          padding: '12px 0',
          marginBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Section:</span>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#d1fae5',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600
            }}>
              NEW - Tabbed navigation (sticky)
            </span>
          </div>
          <div style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: 'white',
            padding: '4px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            {[
              { id: 'fundings', label: 'FUNDINGS', color: '#10b981' },
              { id: 'outreach', label: 'OUTREACH', color: '#3b82f6' },
              { id: 'submissions', label: 'SUBMISSIONS', color: '#ec4899' },
              { id: 'packsource', label: 'PACK SOURCE', color: '#8b5cf6' },
              { id: 'underwriting', label: 'UNDERWRITING', color: '#f59e0b' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => scrollToRepVsRepSection(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: activeSectionTab === tab.id ? '600' : '500',
                  backgroundColor: activeSectionTab === tab.id ? tab.color : 'transparent',
                  color: activeSectionTab === tab.id ? '#fff' : '#475569',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ========== ENHANCEMENT #1: Summary KPI Cards ========== */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Team Performance Summary</h2>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#d1fae5',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600
            }}>
              NEW - Summary at top
            </span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px'
          }}>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Total Revenue (YTD)</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>{formatCurrency(SUMMARY_KPIS.totalRevenue)}</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Total Funded Deals</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>{SUMMARY_KPIS.totalFunded}</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Avg Deal Size</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>{formatCurrency(SUMMARY_KPIS.avgDealSize)}</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Apps to Funded %</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{SUMMARY_KPIS.appsToFundedRate}%</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Activity Volume</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444' }}>{SUMMARY_KPIS.activityVolume.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* ========== ENHANCEMENT #3: Leaderboards (Moved Higher) ========== */}
        <div style={{
          backgroundColor: '#f1f5f9',
          padding: '16px 0',
          marginBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Rankings & Leaderboards</h3>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#d1fae5',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600
            }}>
              ENHANCED - Moved higher
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Total Revenue Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Total Revenue</h4>
              {LEADERBOARD_DATA.byRevenue.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#10b981', fontSize: '13px' }}>{formatCurrency(stat.totalRevenue)}</span>
                </div>
              ))}
            </div>
            {/* Funded Deals Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Funded Deals</h4>
              {LEADERBOARD_DATA.byFundedCount.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#3b82f6', fontSize: '13px' }}>{stat.fundedCount} deals</span>
                </div>
              ))}
            </div>
            {/* Conversion Rate Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Approved→Funded %</h4>
              {LEADERBOARD_DATA.byConversionRate.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#8b5cf6', fontSize: '13px' }}>{stat.conversionRate}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hidden Reps Indicator */}
        {hiddenReps.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <span style={{ color: '#92400e', fontWeight: 500 }}>
              Hidden reps: {hiddenReps.map(r => r.split(' ')[0]).join(', ')}
            </span>
            <button
              onClick={() => setHiddenReps([])}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '13px'
              }}
            >
              Show All
            </button>
          </div>
        )}

        {/* Rep Tabs Navigation */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
            <span style={{ alignSelf: 'center', fontWeight: 600, color: '#374151', marginRight: '8px' }}>Select Rep:</span>
            {REPS.map(rep => (
              <button
                key={rep}
                onClick={() => setActiveRepTab(rep)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: activeRepTab === rep ? '600' : '400',
                  backgroundColor: activeRepTab === rep ? REP_COLORS[rep] : '#e5e7eb',
                  color: activeRepTab === rep ? '#fff' : '#374151'
                }}
              >
                {rep.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>

        {/* ========== FUNDINGS SECTION ========== */}
        <div ref={repVsRepSectionRefs.fundings} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #10b981', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#10b981', fontSize: '1.25rem', fontWeight: 600 }}>FUNDINGS</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Revenue, conversion rates, deal volume and size</p>
            </div>

            {/* Revenue Trends Chart */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Revenue Trends (Jan-Dec)</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                <strong>How to Read:</strong> Each line represents one rep's monthly revenue over time. Upward slope = growth, downward = decline.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={REVENUE_TRENDS_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={REP_COLORS[rep]}
                      strokeWidth={rep === 'Team Average' ? 3 : (rep === activeRepTab ? 4 : 2)}
                      strokeDasharray={rep === 'Team Average' ? "1 3" : undefined}
                      dot={{ r: rep === 'Team Average' ? 6 : (rep === activeRepTab ? 5 : 4) }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
        </div>

        {/* ========== OUTREACH SECTION ========== */}
        <div ref={repVsRepSectionRefs.outreach} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #3b82f6', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#3b82f6', fontSize: '1.25rem', fontWeight: 600 }}>OUTREACH</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Call volume, activity points, and lead generation metrics</p>
            </div>

            {/* Call Volume Trends */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Call Volume Trends (Jan-Dec)</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                <strong>How to Read:</strong> Each line represents one rep's monthly call volume. Activity is the leading indicator of results.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={CALL_VOLUME_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={REP_COLORS[rep]}
                      strokeWidth={rep === 'Team Average' ? 3 : (rep === activeRepTab ? 4 : 2)}
                      strokeDasharray={rep === 'Team Average' ? "1 3" : undefined}
                      dot={{ r: rep === 'Team Average' ? 6 : (rep === activeRepTab ? 5 : 4) }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Activity Points Trends */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Activity Points Trends (Jan-Dec)</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                <strong>How to Read:</strong> Each line represents monthly activity points (Calls + SMS). The ULTIMATE activity metric.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={ACTIVITY_POINTS_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={REP_COLORS[rep]}
                      strokeWidth={rep === 'Team Average' ? 3 : (rep === activeRepTab ? 4 : 2)}
                      strokeDasharray={rep === 'Team Average' ? "1 3" : undefined}
                      dot={{ r: rep === 'Team Average' ? 6 : (rep === activeRepTab ? 5 : 4) }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Apps-In Trends */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Apps-In Trends (Jan-Dec)</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                <strong>How to Read:</strong> Each line represents monthly application submissions. Apps-In is the bridge between activity and revenue.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={APPS_IN_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={REP_COLORS[rep]}
                      strokeWidth={rep === 'Team Average' ? 3 : (rep === activeRepTab ? 4 : 2)}
                      strokeDasharray={rep === 'Team Average' ? "1 3" : undefined}
                      dot={{ r: rep === 'Team Average' ? 6 : (rep === activeRepTab ? 5 : 4) }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
        </div>

        {/* ========== SUBMISSIONS SECTION ========== */}
        <div ref={repVsRepSectionRefs.submissions} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #ec4899', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#ec4899', fontSize: '1.25rem', fontWeight: 600 }}>SUBMISSION ANALYSIS</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Track lender submissions per deal and submission efficiency over time</p>
            </div>

            {/* Side by Side: Avg per Funded + Avg per Underwriting */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
              <div style={{ flex: '1 1 48%', minWidth: '400px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Avg Submissions per Funded Deal</h3>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Lower = more efficient at closing</p>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={AVG_SUBS_PER_FUNDED_DATA}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{fontSize: 10}} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip formatter={(value) => Number(value).toFixed(2)} />
                    <Legend content={renderCustomLegend} />
                    {displayReps.map(rep => (
                      <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 3 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: 3 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: '1 1 48%', minWidth: '400px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Avg Submissions per Underwriting</h3>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Shows early pipeline activity</p>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={AVG_SUBS_PER_UW_DATA}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{fontSize: 10}} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip formatter={(value) => Number(value).toFixed(2)} />
                    <Legend content={renderCustomLegend} />
                    {displayReps.map(rep => (
                      <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 3 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: 3 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Submission Success Rate */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Submission Success Rate</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                <strong>How to Read:</strong> Shows what percentage of submissions result in funded deals. Higher % = better lender targeting.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={SUBMISSION_SUCCESS_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Rep Comparison Bar Charts - 2x2 Grid */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Rep Submission Comparison (All Time)</h3>
                <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '6px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Compare submission behavior across reps. Lower submissions per funded = more efficient.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {/* Chart 1: Submissions per Funded */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Submissions per Funded Deal</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={REP_SUBMISSION_COMPARISON} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => Number(v).toFixed(1)} />
                      <Bar dataKey="avgPerFunded" fill="#3b82f6">
                        <LabelList dataKey="avgPerFunded" position="right" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => v.toFixed(1)} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Chart 2: Decline Rate */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Lender Decline Rate %</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={REP_SUBMISSION_COMPARISON} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" unit="%" />
                      <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                      <Bar dataKey="declineRate" fill="#f59e0b">
                        <LabelList dataKey="declineRate" position="right" style={{ fontSize: 11, fill: '#f59e0b', fontWeight: 600 }} formatter={(v) => `${v}%`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Chart 3: Avg per Deal */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Avg Submissions per Deal</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={REP_SUBMISSION_COMPARISON} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => Number(v).toFixed(1)} />
                      <Bar dataKey="avgPerDeal" fill="#10b981">
                        <LabelList dataKey="avgPerDeal" position="right" style={{ fontSize: 11, fill: '#10b981', fontWeight: 600 }} formatter={(v) => v.toFixed(1)} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Chart 4: Open Submissions */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Open Submissions (Pending)</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={REP_SUBMISSION_COMPARISON} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="openSubmissions" fill="#8b5cf6">
                        <LabelList dataKey="openSubmissions" position="right" style={{ fontSize: 11, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
        </div>

        {/* ========== PACK SOURCE SECTION ========== */}
        <div ref={repVsRepSectionRefs.packsource} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#8b5cf6', fontSize: '1.25rem', fontWeight: 600 }}>PACK SOURCE BREAKDOWN</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Distribution of deals by source across the funnel stages</p>
            </div>

            {/* Month Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>Month:</span>
              <select
                value={packSourceMonth}
                onChange={(e) => setPackSourceMonth(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
              >
                {MONTHS.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Showing: {packSourceMonth}</span>
            </div>

            {/* 4 Pie Charts */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Pack Source Distribution</h3>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '6px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
              {[
                { title: 'Apps In', data: PACK_SOURCE_DATA.appsIn },
                { title: 'Approved', data: PACK_SOURCE_DATA.approved },
                { title: 'Funded', data: PACK_SOURCE_DATA.funded },
                { title: 'Declined/Lost', data: PACK_SOURCE_DATA.declined }
              ].map((chart, idx) => (
                <div key={idx} style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#374151' }}>{chart.title}</h4>
                  <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '11px' }}>Total: {chart.data.reduce((s, d) => s + d.value, 0)}</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} label={({ value }) => value} labelLine={false}>
                        {chart.data.map((entry, i) => (
                          <Cell key={`cell-${i}`} fill={PACK_SOURCE_COLORS[i % PACK_SOURCE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} deals`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>

            {/* Pack Source Trends Stacked Bar */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Apps In by Pack Source - Month over Month</h3>
                <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '6px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Each bar shows the percentage breakdown of Apps In by Pack Source for that month.
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={PACK_SOURCE_TRENDS}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="Inbound" stackId="a" fill="#3b82f6" />
                  <Bar dataKey="Outbound" stackId="a" fill="#10b981" />
                  <Bar dataKey="Referral" stackId="a" fill="#f59e0b" />
                  <Bar dataKey="Missing" stackId="a" fill="#9ca3af" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

        {/* ========== UNDERWRITING SECTION ========== */}
        <div ref={repVsRepSectionRefs.underwriting} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #f59e0b', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '1.25rem', fontWeight: 600 }}>UNDERWRITING</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Application approval rates and underwriting performance</p>
            </div>

            {/* Apps -> Approved Count */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Apps to Approved Count Trends</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Monthly approved application count. Higher = more deals making it through underwriting.</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={APPROVED_TRENDS_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Approval Rate % */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Approval Rate % Trends</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Apps to Approved conversion rate. Higher % = better deal qualification.</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={APPROVAL_RATE_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Apps -> Funded Rate */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Apps to Funded Conversion Rate Trends</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>The MONEY metric - Apps to Funded directly impacts revenue.</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={APPS_TO_FUNDED_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Funded Count */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Funded Deal Count Trends</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>The ultimate measure of sales success - actual closed deals.</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={FUNDED_COUNT_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Avg Deal Size */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Avg Deal Size Trends</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Average deal size reveals targeting strategy and revenue composition.</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={AVG_DEAL_SIZE_DATA}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={REP_COLORS[rep]} strokeWidth={rep === activeRepTab ? 4 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: rep === activeRepTab ? 5 : 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ========== TEAM OVERVIEW SECTION ========== */}
        <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Team Performance Overview</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Aggregate team performance across all reps. Compare historical trends and identify overall patterns.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            {/* Radar Chart */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Rep Performance Scorecard (December)</h3>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Each axis = different performance dimension. Larger area = better overall.</p>
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={RADAR_DATA}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} />
                  {displayReps.map(rep => (
                    <Radar key={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} dataKey={rep} stroke={REP_COLORS[rep]} fill={REP_COLORS[rep]} fillOpacity={rep === activeRepTab ? 0.5 : 0.2} strokeWidth={rep === activeRepTab ? 3 : 1} />
                  ))}
                  <Legend content={renderCustomLegend} />
                  <Tooltip formatter={(value) => value.toFixed(1)} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Bubble Chart */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Efficiency Sweet Spot Finder</h3>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>X = activity, Y = conversion %, Bubble size = revenue. Top-right = ideal.</p>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                  <CartesianGrid />
                  <XAxis type="number" dataKey="x" name="Activity" />
                  <YAxis type="number" dataKey="y" name="Conversion %" />
                  <ZAxis type="number" dataKey="z" range={[50, 500]} />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend content={renderCustomLegend} />
                  {REPS.map(rep => (
                    <Scatter key={rep} name={rep.split(' ')[0]} data={BUBBLE_DATA.filter(d => d.rep === rep)} fill={REP_COLORS[rep]} />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ========== DECLINED/LOST ANALYSIS ========== */}
        <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px' }}>Declined & Lost Opportunities</h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>Track deals that did not close for {activeRepTab.split(' ')[0]}. Use for follow-up opportunities.</p>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '20px', backgroundColor: '#fef2f2', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '8px' }}>Lost Opportunities</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc2626' }}>{DECLINED_LOST_DATA.totalCount}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Deals</div>
            </div>
            <div style={{ padding: '20px', backgroundColor: '#fef2f2', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '8px' }}>Potential Value</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(DECLINED_LOST_DATA.totalValue)}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Amount</div>
            </div>
          </div>

          {/* Top Reasons */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Top Decline Reasons</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {DECLINED_LOST_DATA.topReasons.map((r, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                  <span style={{ fontWeight: 700, color: '#ef4444' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '14px' }}>{r.reason}</span>
                  <span style={{ fontWeight: 600, color: '#6b7280' }}>{r.count} deal{r.count > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Declines Table */}
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Recent Declines (Last 30 Days)</h4>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Follow-up opportunities - these clients may be worth revisiting</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Account</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Opportunity</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Reason</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {DECLINED_LOST_DATA.recentDeclines.map((deal, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px' }}>{deal.Account}</td>
                    <td style={{ padding: '10px' }}>{deal.Opportunity}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(deal.Amount)}</td>
                    <td style={{ padding: '10px' }}>{deal.Stage_Detail}</td>
                    <td style={{ padding: '10px' }}>{deal.Date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enhancement Legend */}
        <div style={{
          padding: '20px',
          backgroundColor: '#eff6ff',
          borderRadius: '12px',
          border: '2px solid #3b82f6'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: '#1e40af' }}>Rep v Rep Tab - Enhancement Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>Enhancement #1</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Summary KPI cards at top before charts</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>Enhancement #2</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Tabbed sub-navigation for 5 sections (STICKY - stays visible)</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>Enhancement #3</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Leaderboards moved higher (Revenue, Funded Deals, Conversion %)</div>
            </div>
          </div>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #93c5fd' }}>
            <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>Complete Content Included:</div>
            <div style={{ fontSize: '12px', color: '#3b82f6' }}>
              FUNDINGS (1 chart) | OUTREACH (3 charts) | SUBMISSIONS (7 items) | PACK SOURCE (6 items) | UNDERWRITING (5 charts) | Team Overview (2 charts) | Declined/Lost Analysis | Leaderboards (3 rankings)
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SINGLE REP DEEP-DIVE SUBTAB - V3 REDESIGN (Scrollable Sections, Not Tabs)
  // ============================================================================

  const renderSingleRepSubtab = () => {
    // Get data for selected rep
    const repGoals = SINGLE_REP_GOALS[selectedSingleRep] || {};
    const repInsights = SINGLE_REP_INSIGHTS[selectedSingleRep] || {};
    const repSubmissions = SINGLE_REP_SUBMISSIONS[selectedSingleRep] || {};
    const repPackSource = SINGLE_REP_PACK_SOURCE[selectedSingleRep] || [];
    const repFirstName = selectedSingleRep.split(' ')[0];

    // Section navigation configuration (V3 - scrollable sections)
    const sectionNavItems = [
      { id: 'snapshot', label: 'Snapshot', icon: '💡', color: '#8b5cf6' },
      { id: 'conversions', label: 'Conversions', icon: '📈', color: '#3b82f6' },
      { id: 'activity', label: 'Activity', icon: '📞', color: '#f59e0b' },
      { id: 'submissions', label: 'Submissions', icon: '📤', color: '#ec4899' }
    ];

    // Calculate 3-month totals for progress bar
    const months = ['October', 'November', 'December'];
    let totalGoal = 0;
    let totalActual = 0;
    months.forEach(month => {
      if (repGoals[month]) {
        totalGoal += repGoals[month].goal;
        totalActual += repGoals[month].actual;
      }
    });
    const overallAttainment = totalGoal > 0 ? (totalActual / totalGoal) * 100 : 0;

    // Card wrapper style (consistent with This Month/This Week)
    const cardWrapperStyle = {
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '24px',
      scrollMarginTop: '80px'
    };

    // Section header style (collapsible)
    const getSectionHeaderStyle = (sectionId, color) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '12px 16px',
      backgroundColor: color,
      borderRadius: collapsedSingleRepSections[sectionId] ? '8px' : '8px 8px 0 0',
      marginBottom: collapsedSingleRepSections[sectionId] ? '0' : '16px'
    });

    return (
      <div>
        {/* ========== REP SELECTOR ========== */}
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '20px',
          padding: '16px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <span style={{ alignSelf: 'center', fontWeight: 600, color: '#374151', marginRight: '8px' }}>
            Select Rep:
          </span>
          {REPS.map(rep => (
            <button
              key={rep}
              onClick={() => setSelectedSingleRep(rep)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: selectedSingleRep === rep ? '600' : '400',
                background: selectedSingleRep === rep ? REP_COLORS[rep] : '#e5e7eb',
                color: selectedSingleRep === rep ? '#fff' : '#374151',
                transition: 'all 0.2s'
              }}
            >
              {rep.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* ========== V3: Sticky "JUMP TO:" Navigation Bar ========== */}
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 10,
          backgroundColor: '#f1f5f9',
          padding: '12px 16px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            JUMP TO:
          </span>
          <span style={{
            padding: '4px 8px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>✨ V3 - Scrollable sections (like This Month/This Week)</span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {sectionNavItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSingleRepSection(item.id)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: activeSingleRepSection === item.id ? '600' : '500',
                  backgroundColor: activeSingleRepSection === item.id ? item.color : '#e2e8f0',
                  color: activeSingleRepSection === item.id ? 'white' : '#4b5563',
                  fontSize: '13px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* ================================================================== */}
        {/* SECTION 1: SNAPSHOT (Merged Insights + Goals) - Always Visible */}
        {/* ================================================================== */}
        <div ref={singleRepSectionRefs.snapshot} style={{...cardWrapperStyle}}>
          {/* Collapsible Header */}
          <div
            onClick={() => toggleSingleRepSection('snapshot')}
            style={getSectionHeaderStyle('snapshot', '#8b5cf6')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>💡</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                {repFirstName}'s Snapshot
              </h3>
              <span style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 600
              }}>✨ MERGED - Insights + Goals</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {collapsedSingleRepSections['snapshot'] ? '▶' : '▼'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!collapsedSingleRepSections['snapshot'] && (
            <div>
              {/* Key Insights Cards */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                  Key Insights
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {/* Card 1: Trend Direction */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.875rem' }}>📈 Trend Direction</h5>
                    <p style={{
                      fontSize: '2rem',
                      fontWeight: 700,
                      margin: '8px 0',
                      color: repInsights.trendDirection === 'improving' ? '#10b981' : '#ef4444'
                    }}>
                      {repInsights.trendDirection === 'improving' ? '↗' : '↘'} {Math.abs(repInsights.trendPercent)}%
                    </p>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      {repInsights.trendDirection === 'improving' ? 'Revenue growing' : 'Revenue declining'} YTD
                    </p>
                  </div>

                  {/* Card 2: Best Month */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.875rem' }}>🏆 Best Month</h5>
                    <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: '8px 0', color: '#10b981' }}>
                      {repInsights.bestMonth}
                    </p>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      {formatCurrency(repInsights.bestRevenue)} revenue
                    </p>
                  </div>

                  {/* Card 3: Coaching Focus */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    borderTop: '3px solid #ef4444'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.875rem' }}>🎯 Coaching Focus</h5>
                    <p style={{ fontSize: '1.25rem', fontWeight: 700, margin: '8px 0', color: '#ef4444' }}>
                      {repInsights.biggestGap}
                    </p>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      {repInsights.gapDetail}
                    </p>
                  </div>
                </div>
              </div>

              {/* 3-Month Progress Bar */}
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Q4 Goal Attainment</span>
                  <span style={{
                    fontWeight: 700,
                    color: overallAttainment >= 100 ? '#10b981' : overallAttainment >= 80 ? '#f59e0b' : '#ef4444'
                  }}>
                    {overallAttainment.toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  height: '12px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(overallAttainment, 100)}%`,
                    backgroundColor: overallAttainment >= 100 ? '#10b981' : overallAttainment >= 80 ? '#f59e0b' : '#ef4444',
                    borderRadius: '6px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  <span>Actual: {formatCurrency(totalActual)}</span>
                  <span>Goal: {formatCurrency(totalGoal)}</span>
                </div>
              </div>

              {/* Monthly Goal Cards */}
              <div>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                  Monthly Goals (Q4)
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {months.map(month => {
                    const goalData = repGoals[month] || { goal: 0, actual: 0 };
                    const attainment = goalData.goal > 0 ? (goalData.actual / goalData.goal) * 100 : 0;
                    return (
                      <div key={month} style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        borderTop: `3px solid ${attainment >= 100 ? '#10b981' : attainment >= 80 ? '#f59e0b' : '#ef4444'}`
                      }}>
                        <h5 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>{month}</h5>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Goal:</span>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(goalData.goal)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Actual:</span>
                          <span style={{ fontWeight: 600, color: attainment >= 100 ? '#10b981' : '#374151' }}>{formatCurrency(goalData.actual)}</span>
                        </div>
                        <div style={{
                          height: '8px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          marginBottom: '8px'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.min(attainment, 100)}%`,
                            backgroundColor: attainment >= 100 ? '#10b981' : attainment >= 80 ? '#f59e0b' : '#ef4444',
                            borderRadius: '4px'
                          }} />
                        </div>
                        <div style={{ textAlign: 'center', fontWeight: 700, color: attainment >= 100 ? '#10b981' : attainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                          {attainment.toFixed(1)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================== */}
        {/* SECTION 1.5: DEAL PERFORMANCE - NEW Enhancement #4 (12-4-25) */}
        {/* ================================================================== */}
        <div ref={singleRepSectionRefs.dealPerformance} style={{...cardWrapperStyle}}>
          {/* Collapsible Header */}
          <div
            onClick={() => toggleSingleRepSection('dealPerformance')}
            style={getSectionHeaderStyle('dealPerformance', '#10b981')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>💲</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Deal Performance
              </h3>
              <span style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 600
              }}>✨ NEW SECTION</span>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {collapsedSingleRepSections['dealPerformance'] ? '▶' : '▼'}
            </span>
          </div>

          {/* Collapsible Content - 2x2 Grid Layout */}
          {!collapsedSingleRepSections['dealPerformance'] && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
              {/* Chart 1: Funded Amount */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Funded Amount
                </h4>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Total deal volume ($)</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SINGLE_REP_DEAL_PERFORMANCE.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.fundedAmount || 0,
                    teamAvg: item['Team Average']?.fundedAmount || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={selectedSingleRep.split(' ')[0]} stroke="#10b981" strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 10, fill: '#10b981', fontWeight: 600 }} formatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#1e293b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 2: Revenue */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Revenue
                </h4>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Commission earned ($)</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SINGLE_REP_DEAL_PERFORMANCE.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.revenue || 0,
                    teamAvg: item['Team Average']?.revenue || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={selectedSingleRep.split(' ')[0]} stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 10, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#1e293b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 3: Deals Funded */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Deals Funded
                </h4>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Count of funded deals</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SINGLE_REP_DEAL_PERFORMANCE.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.dealsFunded || 0,
                    teamAvg: item['Team Average']?.dealsFunded || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => Number(value).toFixed(0)} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={selectedSingleRep.split(' ')[0]} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 10, fill: '#f59e0b', fontWeight: 600 }} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#1e293b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 4: Avg Deal Size (moved from Activity section) */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Avg Deal Size
                </h4>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Average per funded deal</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SINGLE_REP_DEAL_PERFORMANCE.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.avgDealSize || 0,
                    teamAvg: item['Team Average']?.avgDealSize || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={selectedSingleRep.split(' ')[0]} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 10, fill: '#8b5cf6', fontWeight: 600 }} formatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#1e293b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================== */}
        {/* SECTION 2: CONVERSION TRENDS - Always Visible */}
        {/* ================================================================== */}
        <div ref={singleRepSectionRefs.conversions} style={{...cardWrapperStyle}}>
          {/* Collapsible Header */}
          <div
            onClick={() => toggleSingleRepSection('conversions')}
            style={getSectionHeaderStyle('conversions', '#3b82f6')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📈</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Conversion Trends
              </h3>
              <span style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 600
              }}>✨ ENHANCED - Team avg lines</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {collapsedSingleRepSections['conversions'] ? '▶' : '▼'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!collapsedSingleRepSections['conversions'] && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
              {/* Chart 1: Conversion Rate Trends */}
              <div>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Conversion Rate Trends (with Team Average)
                </h4>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SINGLE_REP_CONVERSION_TRENDS.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.appsToApproved || 0,
                    teamAvg: item['Team Average']?.appsToApproved || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="repValue" name={`${repFirstName}'s Rate`} stroke={REP_COLORS[selectedSingleRep]} strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 10, fill: '#374151', fontWeight: 600 }} formatter={(v) => `${Number(v).toFixed(0)}%`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Average" stroke="#000000" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 2: Conversion by Pack Source - ENHANCED to M/O/M multi-line (12-4-25) */}
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Apps → Funded % by Pack Source (M/O/M)
                </h4>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>
                  ✨ ENHANCED - Now shows 12-month trend per pack source
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={PACK_SOURCE_CONVERSION_TRENDS}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="Inbound" name="Inbound" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Outbound" name="Outbound" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Referral" name="Referral" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================== */}
        {/* SECTION 3: ACTIVITY TRENDS - ENHANCED 12-4-25: Now 3 charts (Calls, SMS, Talk Time) */}
        {/* ================================================================== */}
        <div ref={singleRepSectionRefs.activity} style={{...cardWrapperStyle}}>
          {/* Collapsible Header */}
          <div
            onClick={() => toggleSingleRepSection('activity')}
            style={getSectionHeaderStyle('activity', '#f59e0b')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📞</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Activity Trends
              </h3>
              <span style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 600
              }}>✨ ENHANCED - Calls, SMS, Talk Time (12-4-25)</span>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {collapsedSingleRepSections['activity'] ? '▶' : '▼'}
            </span>
          </div>

          {/* Collapsible Content - 3 charts in a row */}
          {!collapsedSingleRepSections['activity'] && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              {/* Chart 1: Calls */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Calls (with Team Average)
                </h4>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={SINGLE_REP_ACTIVITY_TRENDS.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.calls || 0,
                    teamAvg: item['Team Average']?.calls || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(value) => Math.round(value)} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={`${repFirstName}'s Calls`} stroke={REP_COLORS[selectedSingleRep]} strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 9, fill: '#374151', fontWeight: 600 }} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#000000" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 2: SMS */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  SMS (with Team Average)
                </h4>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={SINGLE_REP_ACTIVITY_TRENDS.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.sms || 0,
                    teamAvg: item['Team Average']?.sms || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(value) => Math.round(value)} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={`${repFirstName}'s SMS`} stroke={REP_COLORS[selectedSingleRep]} strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 9, fill: '#374151', fontWeight: 600 }} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#000000" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 3: Talk Time (Minutes) */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Talk Time - Minutes (with Team Average)
                </h4>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={SINGLE_REP_ACTIVITY_TRENDS.map(item => ({
                    month: item.month,
                    repValue: item[selectedSingleRep]?.talkTime || 0,
                    teamAvg: item['Team Average']?.talkTime || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(value) => `${value}m`} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(0)} min`} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="repValue" name={`${repFirstName}'s Talk Time`} stroke={REP_COLORS[selectedSingleRep]} strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="repValue" position="top" style={{ fontSize: 9, fill: '#374151', fontWeight: 600 }} formatter={(v) => `${v}m`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#000000" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================== */}
        {/* SECTION 4: SUBMISSIONS - Always Visible */}
        {/* ================================================================== */}
        <div ref={singleRepSectionRefs.submissions} style={{...cardWrapperStyle}}>
          {/* Collapsible Header */}
          <div
            onClick={() => toggleSingleRepSection('submissions')}
            style={getSectionHeaderStyle('submissions', '#ec4899')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📤</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Submissions
              </h3>
              <span style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 600
              }}>✨ NEW - Full section</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {collapsedSingleRepSections['submissions'] ? '▶' : '▼'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!collapsedSingleRepSections['submissions'] && (
            <div>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#0369a1', marginBottom: '4px' }}>Total Submissions</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#0284c7' }}>{repSubmissions.totalSubmissions}</div>
                </div>
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Won</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#16a34a' }}>{repSubmissions.wonSubmissions}</div>
                </div>
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '4px' }}>Lost</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc2626' }}>{repSubmissions.lostSubmissions}</div>
                </div>
                <div style={{
                  background: '#fefce8',
                  border: '1px solid #fef08a',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#854d0e', marginBottom: '4px' }}>Open</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#ca8a04' }}>{repSubmissions.openSubmissions}</div>
                </div>
              </div>

              {/* Submission Metrics - ENHANCED: Added Avg per Declined (4 cards) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  borderTop: '3px solid #10b981'
                }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>Avg Subs per Funded</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#10b981' }}>{repSubmissions.avgPerFunded}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: 2.9</div>
                </div>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  borderTop: '3px solid #3b82f6'
                }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>Avg Subs per Underwriting</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#3b82f6' }}>{repSubmissions.avgPerUW}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: 3.3</div>
                </div>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  borderTop: '3px solid #ef4444'
                }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>Avg Subs per Declined</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#ef4444' }}>{repSubmissions.avgPerDeclined}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: 4.2</div>
                </div>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  borderTop: '3px solid #8b5cf6'
                }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>Success Rate</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#8b5cf6' }}>{repSubmissions.successRate}%</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: 35.6%</div>
                </div>
              </div>

              {/* Submission Trends Chart - ENHANCED 12-4-25: Added formula subtitle */}
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Submission Success Rate Trend (with Team Average)
                </h4>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>
                  Formula: Funded Deals ÷ Total Submissions × 100
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={SUBMISSION_SUCCESS_DATA.map(item => ({
                    month: item.month,
                    successRate: item[selectedSingleRep] || 0,
                    teamAvg: item['Team Average'] || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="successRate" name={`${repFirstName}'s Success Rate`} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }}>
                      <LabelList dataKey="successRate" position="top" style={{ fontSize: 10, fill: '#8b5cf6', fontWeight: 600 }} formatter={(v) => `${Number(v).toFixed(0)}%`} />
                    </Line>
                    <Line type="monotone" dataKey="teamAvg" name="Team Average" stroke="#000000" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ========== V3 ENHANCEMENT LEGEND ========== */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: '#eff6ff',
          borderRadius: '12px',
          border: '2px solid #3b82f6'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1e40af' }}>
            🔍 Single Rep Deep-Dive V3 Enhancements Applied
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '14px' }}>
            <div><span style={{ color: '#10b981', fontWeight: 600 }}>✨ V3:</span> Scrollable sections with sticky JUMP TO navigation (like This Month/This Week)</div>
            <div><span style={{ color: '#10b981', fontWeight: 600 }}>✨ MERGED:</span> Insights + Goals combined into SNAPSHOT section</div>
            <div><span style={{ color: '#10b981', fontWeight: 600 }}>✨ ENHANCED:</span> Collapsible sections for space management</div>
            <div><span style={{ color: '#10b981', fontWeight: 600 }}>✨ ENHANCED:</span> Team Average comparison lines on all charts</div>
          </div>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #93c5fd' }}>
            <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>4 Scrollable Sections:</div>
            <div style={{ fontSize: '12px', color: '#3b82f6' }}>
              💡 SNAPSHOT (Insights + Goals) | 📈 CONVERSIONS (2 charts) | 📞 ACTIVITY (2 charts) | 📤 SUBMISSIONS (cards + metrics + trend)
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // TEAM SNAPSHOT SUBTAB - COMPLETE IMPLEMENTATION (12-4-25)
  // 6 Enhancements Applied:
  // #1: Reorder Sections (ME-5) - Team Aggregates first
  // #2: Pack Source Tabs (ME-2) - Tab selector for 3 views
  // #3: Sticky Section Tabs - Jump to any section
  // #4: Collapsible Sections - Click header to collapse
  // #5: Card-Style Wrappers - Consistent with other tabs
  // #6: Summary KPI Cards - Quick glance at top
  // ============================================================================

  const renderTeamSnapshotSubtab = () => {
    // Card wrapper style (consistent with other tabs)
    const cardWrapperStyle = {
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '24px',
      scrollMarginTop: '80px'
    };

    // Section header style (collapsible)
    const getTeamSectionHeaderStyle = (sectionId, color) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '12px 16px',
      backgroundColor: color,
      borderRadius: collapsedTeamSections[sectionId] ? '8px' : '8px 8px 0 0',
      marginBottom: collapsedTeamSections[sectionId] ? '0' : '16px'
    });

    // Leaderboard card helper
    const LeaderCard = ({ title, timeframe, data, formatValue }) => (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '12px', textTransform: 'uppercase' }}>{timeframe}</div>
        {data.slice(0, 5).map((item, idx) => (
          <div key={item.rep} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none', fontSize: '0.9rem' }}>
            <span style={{ color: idx < 3 ? '#374151' : '#6b7280' }}>
              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`} {item.rep.split(' ')[0]}
            </span>
            <span style={{ fontWeight: 600, color: idx === 0 ? '#10b981' : idx < 3 ? '#374151' : '#9ca3af' }}>{formatValue(item)}</span>
          </div>
        ))}
      </div>
    );

    return (
      <div>
        {/* ========== ENHANCEMENT #3: Sticky Section Tab Navigation ========== */}
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 10,
          backgroundColor: '#f1f5f9',
          padding: '12px 16px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600, color: '#374151', marginRight: '8px', fontSize: '14px' }}>JUMP TO:</span>
          {[
            { id: 'summary', label: 'SUMMARY', color: '#8b5cf6' },
            { id: 'aggregates', label: 'AGGREGATES', color: '#10b981' },
            { id: 'submissions', label: 'SUBMISSIONS', color: '#3b82f6' },
            { id: 'rankings', label: 'RANKINGS', color: '#f59e0b' },
            { id: 'funding', label: 'FUNDING', color: '#10b981' },
            { id: 'activity', label: 'ACTIVITY', color: '#3b82f6' },
            { id: 'packSource', label: 'PACK SOURCE', color: '#8b5cf6' },
            { id: 'table', label: 'TABLE', color: '#6b7280' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => scrollToTeamSection(tab.id)}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: activeTeamSection === tab.id ? '600' : '500',
                fontSize: '12px',
                background: activeTeamSection === tab.id ? tab.color : '#e2e8f0',
                color: activeTeamSection === tab.id ? '#fff' : '#475569',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
          <span style={{
            marginLeft: 'auto',
            padding: '4px 8px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>✨ NEW - Sticky Navigation</span>
        </div>

        {/* ========== ENHANCEMENT #6: Summary KPI Cards ========== */}
        <div ref={teamSectionRefs.summary} style={{ marginBottom: '24px', scrollMarginTop: '80px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#374151' }}>
              📊 Quick Summary
            </h3>
            <span style={{
              padding: '4px 8px',
              backgroundColor: '#d1fae5',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600
            }}>✨ NEW</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
            <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Revenue (YTD)</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatCurrency(TEAM_SNAPSHOT_KPIS.totalRevenue)}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Funded Deals</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{TEAM_SNAPSHOT_KPIS.totalFundedDeals}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Team Avg Conversion</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{TEAM_SNAPSHOT_KPIS.teamAvgConversion.toFixed(1)}%</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Current Month Attainment</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{TEAM_SNAPSHOT_KPIS.currentMonthAttainment.toFixed(1)}%</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Top Performer</div>
              <div style={{ fontSize: '18px', fontWeight: 700 }}>🏆 {TEAM_SNAPSHOT_KPIS.topPerformer.split(' ')[0]}</div>
            </div>
          </div>
        </div>

        {/* ========== SECTION 1: TEAM AGGREGATES (Moved to FIRST per ME-5) ========== */}
        <div ref={teamSectionRefs.aggregates} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('aggregates')} style={getTeamSectionHeaderStyle('aggregates', '#10b981')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Team Aggregates</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ REORDERED (ME-5)
              </span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['aggregates'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['aggregates'] && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Revenue</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(TEAM_AGGREGATES.totalRevenue)}</div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Funded Dollars</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6' }}>{formatCurrency(TEAM_AGGREGATES.totalFundedAmount)}</div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Funded Units</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#8b5cf6' }}>{TEAM_AGGREGATES.totalFundedUnits.toLocaleString()}</div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Avg Deal Size</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(TEAM_AGGREGATES.avgDealSize)}</div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: `3px solid ${TEAM_AGGREGATES.quotaAttainment >= 100 ? '#10b981' : TEAM_AGGREGATES.quotaAttainment >= 80 ? '#f59e0b' : '#ef4444'}` }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Quota Attainment</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: TEAM_AGGREGATES.quotaAttainment >= 100 ? '#10b981' : TEAM_AGGREGATES.quotaAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                  {TEAM_AGGREGATES.quotaAttainment.toFixed(0)}%
                </div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>December</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: `3px solid ${TEAM_AGGREGATES.activityAttainment >= 100 ? '#10b981' : TEAM_AGGREGATES.activityAttainment >= 80 ? '#f59e0b' : '#ef4444'}` }}>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Activity Attainment</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: TEAM_AGGREGATES.activityAttainment >= 100 ? '#10b981' : TEAM_AGGREGATES.activityAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                  {TEAM_AGGREGATES.activityAttainment.toFixed(0)}%
                </div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>December</div>
              </div>
            </div>
          )}
        </div>

        {/* ========== SECTION 2: TEAM SUBMISSION TRENDS (Moved to SECOND per ME-5) ========== */}
        <div ref={teamSectionRefs.submissions} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('submissions')} style={getTeamSectionHeaderStyle('submissions', '#3b82f6')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📤</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Team Submission Trends</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ REORDERED (ME-5)
              </span>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['submissions'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['submissions'] && (
            <>
              {/* Row 1: 3 charts */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Submissions (Won)</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={TEAM_SUBMISSION_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => Math.round(value)} />
                      <Line type="monotone" dataKey="submissionsWon" stroke="#059669" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="submissionsWon" position="top" style={{ fontSize: 9, fill: '#059669', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Submissions (Lost)</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={TEAM_SUBMISSION_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => Math.round(value)} />
                      <Line type="monotone" dataKey="submissionsLost" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="submissionsLost" position="top" style={{ fontSize: 9, fill: '#dc2626', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Total Declines</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={TEAM_SUBMISSION_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => Math.round(value)} />
                      <Line type="monotone" dataKey="totalDeclines" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="totalDeclines" position="top" style={{ fontSize: 9, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* Row 2: 2 charts */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Avg Submissions per Deal</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={TEAM_SUBMISSION_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => value.toFixed(1)} />
                      <Line type="monotone" dataKey="avgSubmissionsPerDeal" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="avgSubmissionsPerDeal" position="top" style={{ fontSize: 9, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => v.toFixed(1)} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Team Success Rate %</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={TEAM_SUBMISSION_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} unit="%" />
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      <Line type="monotone" dataKey="successRate" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="successRate" position="top" style={{ fontSize: 9, fill: '#8b5cf6', fontWeight: 600 }} formatter={(v) => `${v.toFixed(0)}%`} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ========== SECTION 3: RANKINGS & LEADERBOARDS ========== */}
        <div ref={teamSectionRefs.rankings} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('rankings')} style={getTeamSectionHeaderStyle('rankings', '#f59e0b')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>🏆</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Rankings & Leaderboards</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ Collapsible
              </span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['rankings'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['rankings'] && (
            <>
              {/* Row 1: Results & Hustle (4 cards) */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Results & Hustle</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <LeaderCard title="💰 Revenue Leader" timeframe="December" data={TEAM_RANKINGS.byRevenue} formatValue={(d) => formatCurrency(d.value)} />
                <LeaderCard title="🏆 Most Funded" timeframe="December" data={TEAM_RANKINGS.byFunded} formatValue={(d) => `${d.value} deals`} />
                <LeaderCard title="🎯 Quota Crusher" timeframe="December" data={TEAM_RANKINGS.byQuota} formatValue={(d) => `${d.value.toFixed(0)}%`} />
                <LeaderCard title="📞 Activity King" timeframe="December" data={TEAM_RANKINGS.byActivity} formatValue={(d) => d.value.toLocaleString()} />
              </div>
              {/* Row 2: Efficiency & Consistency (3 cards) */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Efficiency & Consistency</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <LeaderCard title="📈 Best Converter" timeframe="All-Time" data={TEAM_RANKINGS.byConversion} formatValue={(d) => `${d.value.toFixed(1)}%`} />
                <LeaderCard title="💼 Highest Avg Deal" timeframe="All-Time" data={TEAM_RANKINGS.byDealSize} formatValue={(d) => formatCurrency(d.value)} />
                <LeaderCard title="📊 Consistency King" timeframe="All-Time" data={TEAM_RANKINGS.byConsistency} formatValue={(d) => formatCurrency(d.value)} />
              </div>
            </>
          )}
        </div>

        {/* ========== SECTION 4: TEAM REVENUE VS GOAL ========== */}
        <div ref={teamSectionRefs.funding} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('funding')} style={getTeamSectionHeaderStyle('funding', '#10b981')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>💰</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Team Revenue vs Goal</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ Collapsible
              </span>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>
                🏷️ 12/5 LABELS
              </span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['funding'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['funding'] && (
            <>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Monthly team revenue compared to combined team goals</p>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={TEAM_REVENUE_VS_GOAL} margin={{ right: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="Team Revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 5 }}>
                    <LabelList dataKey="revenue" position="top" style={{ fontSize: 10, fill: '#10b981', fontWeight: 600 }} formatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  </Line>
                  <Line type="monotone" dataKey="goal" name="Team Goal" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>

              {/* Team Funded Deals */}
              <h4 style={{ margin: '24px 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>🏆 Team Funded Deals</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={TEAM_FUNDED_DEALS}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 'auto']} padding={{ top: 20 }} />
                  <Tooltip formatter={(value) => Math.round(value)} />
                  <Bar dataKey="funded" name="Funded Deals" fill="#8b5cf6">
                    <LabelList dataKey="funded" position="top" style={{ fontSize: 11, fill: '#8b5cf6', fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Team Apps In */}
              <h4 style={{ margin: '24px 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>📋 Team Apps In</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={TEAM_APPS_IN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 'auto']} padding={{ top: 20 }} />
                  <Tooltip formatter={(value) => Math.round(value)} />
                  <Bar dataKey="appsIn" name="Apps In" fill="#3b82f6">
                    <LabelList dataKey="appsIn" position="top" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        {/* ========== SECTION 5: TEAM ACTIVITY VOLUME ========== */}
        <div ref={teamSectionRefs.activity} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('activity')} style={getTeamSectionHeaderStyle('activity', '#3b82f6')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📞</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Team Activity Volume</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ Collapsible
              </span>
              <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['activity'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['activity'] && (
            <>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Stacked Calls/SMS showing % breakdown</p>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={TEAM_ACTIVITY_VOLUME}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 'auto']} padding={{ top: 20 }} />
                  <Tooltip formatter={(value) => Math.round(value)} />
                  <Legend />
                  <Bar dataKey="calls" name="Calls" fill="#3b82f6" stackId="activity">
                    <LabelList dataKey="calls" position="center" style={{ fontSize: 10, fill: '#fff', fontWeight: 600 }} />
                  </Bar>
                  <Bar dataKey="sms" name="SMS" fill="#ef4444" stackId="activity">
                    <LabelList dataKey="sms" position="top" style={{ fontSize: 10, fill: '#ef4444', fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        {/* ========== SECTION 6: PACK SOURCE (TABBED - Enhancement #2) ========== */}
        <div ref={teamSectionRefs.packSource} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('packSource')} style={getTeamSectionHeaderStyle('packSource', '#8b5cf6')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Pack Source Analysis</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ TABBED (ME-2)
              </span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['packSource'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['packSource'] && (
            <>
              {/* Tab Selector */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', padding: '8px', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                {[
                  { id: 'volume', label: 'By Volume' },
                  { id: 'revenue', label: 'By Revenue' },
                  { id: 'trend', label: 'Trend' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setPackSourceViewTab(tab.id)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: packSourceViewTab === tab.id ? '600' : '400',
                      background: packSourceViewTab === tab.id ? '#8b5cf6' : 'transparent',
                      color: packSourceViewTab === tab.id ? '#fff' : '#475569',
                      transition: 'all 0.2s'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {packSourceViewTab === 'volume' && (
                <div>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>Pack Source by Volume (All-Time)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={TEAM_PACK_SOURCE_VOLUME} layout="vertical" margin={{ left: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={60} />
                      <Tooltip formatter={(value) => Math.round(value)} />
                      <Legend />
                      <Bar dataKey="appsIn" name="Apps In" fill="#3b82f6" />
                      <Bar dataKey="approved" name="Approved" fill="#8b5cf6" />
                      <Bar dataKey="funded" name="Funded" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {packSourceViewTab === 'revenue' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#374151' }}>Pack Source by Revenue (All-Time)</h4>
                    <span style={{ padding: '4px 8px', backgroundColor: '#dbeafe', borderRadius: '6px', fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>🏷️ 12/5 LABELS</span>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={TEAM_PACK_SOURCE_REVENUE} layout="vertical" margin={{ left: 70, right: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={60} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="revenue" name="Revenue" fill="#10b981">
                        <LabelList dataKey="revenue" position="right" style={{ fontSize: 11, fill: '#10b981', fontWeight: 600 }} formatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {packSourceViewTab === 'trend' && (
                <div>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>Pack Source Trend (Month-by-Month)</h4>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={PACK_SOURCE_TRENDS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => Math.round(value)} />
                      <Legend />
                      <Bar dataKey="Inbound" stackId="packSource" fill="#3b82f6" />
                      <Bar dataKey="Outbound" stackId="packSource" fill="#10b981" />
                      <Bar dataKey="Referral" stackId="packSource" fill="#f59e0b" />
                      <Bar dataKey="Missing" stackId="packSource" fill="#9ca3af" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>

        {/* ========== SECTION 7: MONTHLY PERFORMANCE TABLE ========== */}
        <div ref={teamSectionRefs.table} style={cardWrapperStyle}>
          <div onClick={() => toggleTeamSection('table')} style={getTeamSectionHeaderStyle('table', '#6b7280')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Monthly Performance by Rep</h3>
              <span style={{ padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: 600 }}>
                ✨ Collapsible
              </span>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>{collapsedTeamSections['table'] ? '▶' : '▼'}</span>
          </div>
          {!collapsedTeamSections['table'] && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Rep</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Revenue</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Funded</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Apps</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Calls</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Conversion</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Goal %</th>
                  </tr>
                </thead>
                <tbody>
                  {TEAM_MONTHLY_PERFORMANCE.map((repData, idx) => (
                    <React.Fragment key={repData.rep}>
                      {/* Rep Summary Row (clickable) */}
                      <tr
                        onClick={() => toggleRepRow(repData.rep)}
                        style={{
                          background: '#e5e7eb',
                          cursor: 'pointer',
                          borderBottom: '1px solid #d1d5db'
                        }}
                      >
                        <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          {expandedRepRows[repData.rep] ? '▼' : '▶'} {repData.rep}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                          {formatCurrency(repData.totalRevenue)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>{repData.totalFunded}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>{repData.totalApps}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>{repData.totalCalls.toLocaleString()}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: repData.avgConversion > 45 ? '#10b981' : repData.avgConversion > 35 ? '#f59e0b' : '#ef4444' }}>
                          {repData.avgConversion.toFixed(0)}%
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: repData.avgGoalAttainment >= 100 ? '#10b981' : repData.avgGoalAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                          {repData.avgGoalAttainment.toFixed(0)}%
                        </td>
                      </tr>
                      {/* Expanded Monthly Rows */}
                      {expandedRepRows[repData.rep] && repData.monthlyData.map((monthData, mIdx) => (
                        <tr key={`${repData.rep}-${monthData.month}`} style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: mIdx % 2 === 0 ? '#fff' : '#fafafa'
                        }}>
                          <td style={{ padding: '8px 12px', paddingLeft: '32px', color: '#6b7280' }}>{monthData.month}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#10b981' }}>{formatCurrency(monthData.revenue)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{monthData.funded}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{monthData.appsIn}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{monthData.calls}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: monthData.conversion > 45 ? '#10b981' : monthData.conversion > 35 ? '#f59e0b' : '#ef4444' }}>
                            {monthData.conversion.toFixed(0)}%
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', color: monthData.goalAttainment >= 100 ? '#10b981' : monthData.goalAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                            {monthData.goalAttainment.toFixed(0)}%
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ========== ENHANCEMENT LEGEND ========== */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '12px',
          border: '2px solid #3b82f6'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#1e40af', fontSize: '16px', fontWeight: 600 }}>
            ✨ Team Snapshot - 6 Enhancements Applied
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>1. Section Reorder (ME-5)</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Team Aggregates moved to first, Submissions second</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>2. Pack Source Tabs (ME-2)</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>3 views in one section with tab selector</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>3. Sticky Section Tabs</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Jump to any section with one click</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>4. Collapsible Sections</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Click any section header to collapse/expand</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>5. Card-Style Wrappers</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Consistent with This Month/This Week tabs</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>6. Summary KPI Cards</div>
              <div style={{ fontSize: '13px', color: '#3b82f6' }}>Quick glance metrics at the very top</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Mockup Banner */}
      <div style={{
        padding: '12px 20px',
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px' }}>MOCKUP MODE</span>
        <div>
          <strong style={{ color: '#92400e' }}>Historical Tab - COMPLETE REBUILD</strong>
          <span style={{ color: '#92400e', marginLeft: '8px' }}>This is a proposed design with ALL content from live dashboard + 3 enhancements</span>
        </div>
      </div>

      {/* Tab Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 8px' }}>Historical Performance</h1>
        <p style={{ color: '#6b7280', margin: 0 }}>Advanced analytics and visualizations across all time periods</p>
      </div>

      {/* Subtab Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        padding: '8px',
        backgroundColor: '#f1f5f9',
        borderRadius: '12px'
      }}>
        <button
          onClick={() => setActiveSubtab('rep-vs-rep')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeSubtab === 'rep-vs-rep' ? '600' : '500',
            backgroundColor: activeSubtab === 'rep-vs-rep' ? '#3b82f6' : 'transparent',
            color: activeSubtab === 'rep-vs-rep' ? '#fff' : '#475569'
          }}
        >
          Rep v Rep
        </button>
        <button
          onClick={() => setActiveSubtab('single-rep')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeSubtab === 'single-rep' ? '600' : '500',
            backgroundColor: activeSubtab === 'single-rep' ? '#8b5cf6' : 'transparent',
            color: activeSubtab === 'single-rep' ? '#fff' : '#475569'
          }}
        >
          Single Rep Deep-Dive
        </button>
        <button
          onClick={() => setActiveSubtab('team-snapshot')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeSubtab === 'team-snapshot' ? '600' : '500',
            backgroundColor: activeSubtab === 'team-snapshot' ? '#10b981' : 'transparent',
            color: activeSubtab === 'team-snapshot' ? '#fff' : '#475569'
          }}
        >
          Team Snapshot
        </button>
      </div>

      {/* Subtab Content */}
      {activeSubtab === 'rep-vs-rep' && renderRepVsRepSubtab()}
      {activeSubtab === 'single-rep' && renderSingleRepSubtab()}
      {activeSubtab === 'team-snapshot' && renderTeamSnapshotSubtab()}
    </div>
  );
}
